import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  DollarSign,
  Ticket,
  Users,
  Trophy,
  TrendingUp,
  Activity,
  Gift,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Package,
  UserPlus,
  Wallet,
  CreditCard,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { subDays, endOfDay, startOfMonth, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Progress } from '@ouropix/ui';
import { ScrollArea } from '@ouropix/ui';
import { maskName } from '@ouropix/shared';
import { api } from '@/lib/api';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { RevenueChart } from '@/components/dashboard/RevenueChart';
import { TopGamesTable } from '@/components/dashboard/TopGamesTable';
import { ActiveUsersWidget } from '@/components/dashboard/ActiveUsersWidget';
import { RealtimeMetricsBar } from '@/components/dashboard/RealtimeMetricsBar';
import { DrawAuditTrail } from '@/components/dashboard/DrawAuditTrail';
import { DateRangeFilter, DateRange } from '@/components/shared/DateRangeFilter';
import { useDashboardKpis, StatPeriod } from '@/hooks/useDashboardKpis';
import { useDashboardRevenueChart } from '@/hooks/useDashboardRevenueChart';
import { formatCurrency } from '@/lib/formatters';

export const Route = createFileRoute('/_admin/dashboard')({
  component: DashboardPage,
});

const PERIODS: { value: StatPeriod; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Esta Semana' },
  { value: '15days', label: 'Últimos 15 dias' },
  { value: 'month', label: 'Este Mês' },
];

interface UnifiedPrize {
  id: string;
  user_name: string | null;
  amount: number;
  description: string | null;
  created_at: string;
  prize_type: 'cash' | 'physical' | 'draw';
}

function DashboardPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<StatPeriod>('today');

  const { data: kpis, isPending: kpisPending } = useDashboardKpis(selectedPeriod);
  const { data: kpisReal } = useDashboardKpis(selectedPeriod, 'real');
  const { data: kpisTest } = useDashboardKpis(selectedPeriod, 'test');
  const { data: revenueData } = useDashboardRevenueChart();

  const [breakdownDateRange, setBreakdownDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfDay(new Date()),
  });
  const [breakdownMode, setBreakdownMode] = useState<'all' | 'real' | 'test'>('all');

  const [prizesDateRange, setPrizesDateRange] = useState<DateRange>({
    from: subDays(new Date(), 27),
    to: endOfDay(new Date()),
  });

  // Transacoes recentes
  const { data: recentTransactions } = useQuery({
    queryKey: ['recent-transactions'],
    queryFn: () =>
      api.get('/admin/transactions/recent', { params: { limit: 20 } }).then((r) => r.data as {
        id: string;
        user_name: string;
        type: string;
        amount: number;
        description: string | null;
        created_at: string;
        is_test: boolean;
      }[]),
    staleTime: 15000,
    refetchInterval: 10000,
  });

  // Prêmios unificados
  const { data: recentPrizes } = useQuery({
    queryKey: ['recent-prizes-unified', prizesDateRange.from, prizesDateRange.to, breakdownMode],
    queryFn: async (): Promise<UnifiedPrize[]> => {
      const from = prizesDateRange.from.toISOString();
      const to = endOfDay(prizesDateRange.to).toISOString();
      const r = await api.get('/admin/financial/draw-winners', {
        params: { date_from: from, date_to: to, limit: 50, mode: breakdownMode },
      });
      // Backend retorna { rows, totalCredited, pagination } — desembrulhar pra array
      return (r.data?.rows ?? r.data ?? []) as UnifiedPrize[];
    },
    staleTime: 15000,
    refetchInterval: 15000,
  });

  // Breakdown financeiro
  const { data: transactionBreakdown } = useQuery({
    queryKey: ['transaction-breakdown', breakdownDateRange.from, breakdownDateRange.to, breakdownMode],
    queryFn: async () => {
      try {
        const from = breakdownDateRange.from.toISOString();
        const to = endOfDay(breakdownDateRange.to).toISOString();
        const r = await api.get('/admin/financial/revenue', {
          params: { date_from: from, date_to: to, mode: breakdownMode },
        });
        const d = r.data ?? {};
        const bd = d.breakdown ?? {};
        const k = d.kpis ?? {};
        return {
          deposits: Number(bd.deposits ?? d.deposits ?? 0) || 0,
          purchases: Number(bd.purchases ?? k.totalRevenue ?? d.totalRevenue ?? 0) || 0,
          prizes: Number(bd.prizes ?? k.totalPrizes ?? d.totalPrizes ?? 0) || 0,
          refunds: Number(bd.refunds ?? 0) || 0,
        };
      } catch {
        return { deposits: 0, purchases: 0, prizes: 0, refunds: 0 };
      }
    },
    staleTime: 30000,
    refetchInterval: 30000,
  });

  // Progresso de jogos ativos
  const { data: gamesProgress } = useQuery({
    queryKey: ['games-progress'],
    queryFn: async () => {
      // admin controller: GET /api/admin/games (no-store) — evita cache CDN do /api/games publico
      const r = await api.get('/admin/games', { params: { status: 'active' } });
      const games = r.data?.games ?? r.data?.data ?? (Array.isArray(r.data) ? r.data : []);
      return (games as {
        id: string;
        title: string;
        total_plays: number;
        used_plays: number;
        play_price: number;
      }[]).map((game) => {
        const sold = game.used_plays || 0;
        const total = game.total_plays || 0;
        const remaining = Math.max(total - sold, 0);
        const percentage = total > 0 ? ((sold / total) * 100).toFixed(1) : '0';
        return {
          id: game.id,
          title: game.title,
          total,
          sold,
          remaining,
          percentage,
          revenue: sold * Number(game.play_price),
        };
      });
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit': return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'purchase': return <ArrowUpRight className="h-4 w-4 text-blue-500" />;
      case 'prize': return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 'refund': return <ArrowDownRight className="h-4 w-4 text-red-500" />;
      default: return <DollarSign className="h-4 w-4" />;
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'deposit': return 'Depósito';
      case 'purchase': return 'Compra';
      case 'prize': return 'Prêmio';
      case 'refund': return 'Reembolso';
      default: return type;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'deposit': return 'bg-green-500/10 text-green-500';
      case 'purchase': return 'bg-blue-500/10 text-blue-500';
      case 'prize': return 'bg-yellow-500/10 text-yellow-500';
      case 'refund': return 'bg-red-500/10 text-red-500';
      default: return 'bg-muted';
    }
  };

  const getPrizeBadge = (type: 'cash' | 'physical' | 'draw') => {
    switch (type) {
      case 'cash': return <Badge variant="outline" className="text-xs border-green-500/40 text-green-500">Cash</Badge>;
      case 'physical': return <Badge variant="outline" className="text-xs border-blue-500/40 text-blue-500">Fisico</Badge>;
      case 'draw': return <Badge variant="outline" className="text-xs border-yellow-500/40 text-yellow-500">Sorteio</Badge>;
    }
  };

  const getLiveBadge = () => (
    <Badge variant="outline" className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
      Live
    </Badge>
  );

  const periodLabel = PERIODS.find((p) => p.value === selectedPeriod)?.label || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard Administrativo</h1>
          <p className="text-muted-foreground">Visão geral do desempenho da plataforma</p>
        </div>
      </div>

      <RealtimeMetricsBar />

      {/* Period Selector */}
      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filtro de Período do Dashboard">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setSelectedPeriod(p.value)}
            className={[
              'inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              selectedPeriod === p.value
                ? 'bg-primary text-primary-foreground shadow hover:bg-primary/90'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
            ].join(' ')}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPIs Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" role="region" aria-label="Métricas de Desempenho (KPIs)">
        <KpiCard
          icon={DollarSign}
          title={`Receita — ${periodLabel}`}
          value={kpisPending ? '...' : formatCurrency(kpis?.totalRevenue ?? 0)}
          trend={kpis ? Math.round((kpis.revenueChange ?? 0) * 10) / 10 : undefined}
          trendLabel="vs período anterior"
        />
        <KpiCard
          icon={Ticket}
          title={`Tickets Vendidos — ${periodLabel}`}
          value={kpisPending ? '...' : (kpis?.ticketsSold ?? 0).toLocaleString('pt-BR')}
          trend={kpis ? Math.round((kpis.ticketsSoldChange ?? 0) * 10) / 10 : undefined}
          trendLabel="vs período anterior"
        />
        <KpiCard
          icon={Users}
          title="Usuários Ativos"
          value={kpisPending ? '...' : (kpis?.activeUsers ?? 0).toLocaleString('pt-BR')}
          trendLabel="últimos 30 dias"
          badge={getLiveBadge()}
        />
        <KpiCard
          icon={Trophy}
          title={`Prêmios em Dinheiro — ${periodLabel}`}
          value={kpisPending ? '...' : formatCurrency(kpis?.prizesDistributed ?? 0)}
          trend={kpis ? Math.round((kpis.prizesDistributedChange ?? 0) * 10) / 10 : undefined}
          trendLabel="vs período anterior"
        />
      </div>

      {/* KPI Split Real vs Teste */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" role="region" aria-label="Divisão Real vs Teste">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4 text-green-500" />
              Receita — Real vs Teste
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Real</span>
              <span className="font-semibold text-green-500">
                {kpisPending ? '...' : formatCurrency(kpisReal?.totalRevenue ?? 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                Teste
                <span className="inline-flex items-center rounded-full bg-orange-500/15 text-orange-700 font-semibold uppercase tracking-wider border border-orange-500/30 px-1.5 py-0.5 text-[9px]">
                  teste
                </span>
              </span>
              <span className="font-semibold text-orange-500">
                {kpisPending ? '...' : formatCurrency(kpisTest?.totalRevenue ?? 0)}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Ticket className="h-4 w-4 text-blue-500" />
              Tickets Vendidos — Real vs Teste
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Real</span>
              <span className="font-semibold">
                {kpisPending ? '...' : (kpisReal?.ticketsSold ?? 0).toLocaleString('pt-BR')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                Teste
                <span className="inline-flex items-center rounded-full bg-orange-500/15 text-orange-700 font-semibold uppercase tracking-wider border border-orange-500/30 px-1.5 py-0.5 text-[9px]">
                  teste
                </span>
              </span>
              <span className="font-semibold text-orange-500">
                {kpisPending ? '...' : (kpisTest?.ticketsSold ?? 0).toLocaleString('pt-BR')}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Prêmios — Dinheiro vs Físico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-green-500" />
                Dinheiro
              </span>
              <span className="font-semibold text-green-500">
                {kpisPending ? '...' : formatCurrency(kpis?.prizesDistributed ?? 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-blue-500" />
                Físico
              </span>
              <span className="font-semibold text-blue-500">
                {kpisPending ? '...' : formatCurrency(kpis?.prizesPhysical ?? 0)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Widgets — Cadastros & Saques */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" role="region" aria-label="Cadastros e Saques Pendentes">
        <KpiCard
          icon={UserPlus}
          title="Cadastros Hoje"
          value={kpisPending ? '...' : (kpis?.newRegistrationsToday ?? 0).toLocaleString('pt-BR')}
          trendLabel="novos usuários hoje"
        />
        <KpiCard
          icon={Users}
          title="Cadastros na Semana"
          value={kpisPending ? '...' : (kpis?.newRegistrationsWeek ?? 0).toLocaleString('pt-BR')}
          trendLabel="novos usuários esta semana"
        />
        <KpiCard
          icon={Wallet}
          title="Saques Pendentes"
          value={kpisPending ? '...' : (kpis?.pendingWithdrawals ?? 0).toLocaleString('pt-BR')}
          trendLabel="solicitações aguardando"
        />
        <KpiCard
          icon={DollarSign}
          title="Valor Saques Pendentes"
          value={kpisPending ? '...' : formatCurrency(kpis?.pendingWithdrawalsAmount ?? 0)}
          trendLabel="total a liberar"
        />
      </div>

      {/* Breakdown e Transações */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Fluxo Financeiro */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Fluxo Financeiro
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex gap-1" role="group" aria-label="Filtro modo">
                  {(['all', 'real', 'test'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setBreakdownMode(m)}
                      className={[
                        'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium transition-colors',
                        breakdownMode === m
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                      ].join(' ')}
                    >
                      {m === 'all' ? 'Tudo' : m === 'real' ? 'Real' : 'Teste'}
                    </button>
                  ))}
                </div>
                <DateRangeFilter value={breakdownDateRange} onChange={setBreakdownDateRange} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {transactionBreakdown && (
              <div className="space-y-4" role="list">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm">Depositos</span>
                  </div>
                  <span className="font-semibold text-green-500">+{formatCurrency(transactionBreakdown.deposits)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm">Compras</span>
                  </div>
                  <span className="font-semibold text-blue-500">{formatCurrency(transactionBreakdown.purchases)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-sm">Prêmios Pagos</span>
                  </div>
                  <span className="font-semibold text-yellow-500">-{formatCurrency(transactionBreakdown.prizes)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm">Reembolsos</span>
                  </div>
                  <span className="font-semibold text-red-500">-{formatCurrency(transactionBreakdown.refunds)}</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">Saldo Liquido</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(
                        transactionBreakdown.deposits +
                          transactionBreakdown.purchases -
                          transactionBreakdown.prizes -
                          transactionBreakdown.refunds,
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transações Recentes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Transações em Tempo Real
              {getLiveBadge()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                {recentTransactions && recentTransactions.length > 0 ? (
                  recentTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${getTransactionColor(transaction.type)}`}>
                          {getTransactionIcon(transaction.type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-sm">{maskName(transaction.user_name)}</p>
                            {transaction.is_test && (
                              <span className="inline-flex items-center rounded-full bg-orange-500/15 text-orange-700 font-semibold uppercase tracking-wider border border-orange-500/30 px-1.5 py-0.5 text-[9px]">
                                teste
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {getTransactionLabel(transaction.type)}
                            {transaction.description && ` - ${transaction.description}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`font-semibold ${
                            transaction.type === 'deposit' || transaction.type === 'purchase'
                              ? 'text-green-500'
                              : 'text-red-500'
                          }`}
                        >
                          {transaction.type === 'prize' || transaction.type === 'refund' ? '-' : '+'}
                          {formatCurrency(Math.abs(Number(transaction.amount)))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(transaction.created_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-8">Nenhuma transação recente</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Prêmios Ganhos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-yellow-500" />
              Prêmios Ganhos
            </CardTitle>
            <div className="flex items-center gap-3">
              <DateRangeFilter value={prizesDateRange} onChange={setPrizesDateRange} />
              {getLiveBadge()}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[250px] pr-4">
            <div className="space-y-3">
              {recentPrizes && recentPrizes.length > 0 ? (
                recentPrizes.map((prize) => (
                  <div
                    key={prize.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-yellow-500/10">
                        {prize.prize_type === 'physical' ? (
                          <Package className="h-4 w-4 text-blue-500" />
                        ) : (
                          <Trophy className="h-4 w-4 text-yellow-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{maskName(prize.user_name)}</p>
                          {getPrizeBadge(prize.prize_type)}
                        </div>
                        <p className="text-xs text-muted-foreground">{prize.description || 'Prêmio'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-yellow-500">{formatCurrency(prize.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {prize.created_at &&
                          formatDistanceToNow(new Date(prize.created_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">Nenhum prêmio ganho no período</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid gap-4 md:grid-cols-2" role="region" aria-label="Gráficos e Performance">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Receita dos Últimos 7 Dias
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RevenueChart data={revenueData ?? []} />
          </CardContent>
        </Card>

        {/* Estatisticas Adicionais */}
        <div className="grid gap-4 grid-rows-3">
          <Card>
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Margem de Lucro</p>
                <p className="text-3xl font-bold">{(kpis?.profitMargin ?? 0).toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground mt-1">Receita menos prêmios em dinheiro (físicos à parte)</p>
              </div>
              <div
                className={`p-3 rounded-lg ${
                  (kpis?.profitMargin ?? 0) >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'
                }`}
              >
                <TrendingUp
                  className={`h-6 w-6 ${
                    (kpis?.profitMargin ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}
                />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ticket Medio</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(
                    (kpis?.ticketsSold ?? 0) > 0
                      ? (kpis?.totalRevenue ?? 0) / (kpis?.ticketsSold ?? 1)
                      : 0,
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Receita media por ticket vendido</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <Ticket className="h-6 w-6 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">LTV por Usuário</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(
                    (kpis?.activeUsers ?? 0) > 0
                      ? (kpis?.totalRevenueAlltime ?? 0) / (kpis?.activeUsers ?? 1)
                      : 0,
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Lifetime value medio (receita total)</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Usuários Ativos Widget + Top Jogos */}
      <div className="grid gap-4 md:grid-cols-2">
        <ActiveUsersWidget />
        <TopGamesTable period={selectedPeriod} />
      </div>

      {/* Progresso de Jogos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Jogos Cadastrados — Progresso de Vendas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {gamesProgress && gamesProgress.length > 0 ? (
            <div className="space-y-6">
              {gamesProgress.map((game) => (
                <div key={game.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold">{game.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {game.sold} vendidos de {game.total} • {game.remaining} restantes
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{game.percentage}%</div>
                      <p className="text-sm text-muted-foreground">{formatCurrency(game.revenue)}</p>
                    </div>
                  </div>
                  <Progress value={Number(game.percentage)} className="h-2" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">Nenhum jogo ativo</p>
          )}
        </CardContent>
      </Card>

      {/* AP-01 — Trilha de auditoria de sorteios (LOTEP compliance) */}
      <DrawAuditTrail />
    </div>
  );
}
